<template>
  <div class="rounded-lg shadow-solid-xs border shadow-gray-brand-600 border-gray-brand-600">
    <Modal :is-open="showCancelModal" @close="closeCancelModal">
      <template #title>
        <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle">
          All the good times we had together
        </h2>
      </template>

      <template #body>
        <div class="sm:flex sm:items-start">
          <div class="text-center sm:mt-0 sm:text-left w-full">
            <div>
              <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-4">
                Here's what you'll miss out on if you cancel
              </h2>
              <!-- Benefits List -->
              <div class="text-white rounded-xl bg-gray-900 pt-[14px] pb-[16px] px-[22px]">
                <ul class="mt-[8px] mb-[12px] space-y-2">
                  <li v-for="reward in sortedMembershipPassRewards" :key="reward.id" class="flex">
                    <span class="flex-shrink-0 mt-[-2px]">✅</span>
                    <span class="text-[15px] ml-3">{{ formatReward(reward) }}</span>
                  </li>
                </ul>
              </div>
              <div class="mt-6">
                <span class="text-[15px]">
                  Custom message that can set by the merchant goes here and could be on a couple of lines
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="sm:flex sm:flex-row gap-1">
          <button
            type="button"
            class="px-4 py-2 text-white border border-transparent italic font-semibold text-xs rounded-full bg-gray-900 hover:border-gray-brand-600 sm:mr-2"
            @click="closeCancelModal"
          >
            Back To My Account
          </button>
          <button
            type="button"
            class="px-4 py-2 text-gray-brand-600 border border-gray-brand-600 italic font-semibold text-xs rounded-full bg-white hover:bg-gray-brand-100"
            @click="confirmCancel"
          >
            Cancel Pass Subscription
          </button>
        </div>
      </template>
    </Modal>

    <Modal :is-open="showFeedbackModal" @close="closeCancelModal">
      <template #title>
        <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle">
          We're sorry to see you go
        </h2>
      </template>

      <template #body>
        <div class="sm:flex sm:items-start">
          <div class="w-full">
            <h2 class="pr-3 font-bold text-lg leading-5 tracking-tighter mb-4">
              Please tell us how we could improve the {{ membershipPass?.attributes?.name }} to give more value in the
              future.
            </h2>
            <textarea
              class="w-full h-32 p-3 border rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-brand-600 sm:text-sm"
              :class="{
                'border-gray-300 focus:border-gray-brand-600': !showFeedbackError,
                'border-red-500 focus:border-red-500 focus:ring-red-500': showFeedbackError,
                'placeholder:font-bold placeholder:italic': true
              }"
              placeholder="Add feedback here"
              v-model="feedbackText"
              @input="showFeedbackError = false"
            ></textarea>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="sm:flex sm:flex-row gap-1">
          <button
            type="button"
            class="px-4 py-2 text-white border border-transparent font-semibold italic text-xs rounded-full bg-gray-900 hover:border-gray-brand-600 sm:mr-2"
            @click="closeCancelModal"
          >
            Back To My Account
          </button>
          <button
            type="button"
            class="px-4 py-2 text-gray-brand-600 border border-gray-brand-600 font-semibold italic text-xs rounded-full bg-white hover:bg-gray-brand-100"
            @click="submitFeedback"
          >
            Continue with Cancellation
          </button>
        </div>
      </template>
    </Modal>

    <div class="relative">
      <!-- Pass Row -->
      <div class="flex w-full border-b">
        <div class="w-1/5 flex items-center">
          <div
            class="w-auto mx-auto inline-block text-xxs sm:text-xs uppercase font-manrope font-semibold tracking-widest px-2 py-1 text-brand-800"
          >
            <div class="flex flex-col items-center min-w-[60px]">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="text-brand-800"
              >
                <path
                  d="M7.63599 5.09084C7.63943 5.08101 7.64115 5.0761 7.64207 5.07348C8.42622 2.84782 11.5738 2.84782 12.3579 5.07348C12.3589 5.0761 12.3683 5.10311 12.3872 5.15712V5.15712C12.7215 6.11276 13.6288 6.76833 14.6412 6.76833H14.8449C17.0998 6.76833 18.0261 9.66226 16.1904 10.9716V10.9716C15.3515 11.57 15.0113 12.6583 15.3516 13.6309V13.6309C16.1059 15.7872 13.6884 17.6923 11.7947 16.4145L11.4131 16.157C11.399 16.1475 11.3919 16.1427 11.3854 16.1384C10.5463 15.5797 9.45371 15.5797 8.61457 16.1384C8.60807 16.1427 8.601 16.1475 8.58687 16.157L8.22219 16.4031C6.32314 17.6846 3.89862 15.7742 4.65512 13.6117V13.6117C4.99238 12.6477 4.66314 11.5687 3.83985 10.9642V10.9642C2.03051 9.63578 2.97013 6.76833 5.21477 6.76833H5.36556C6.37392 6.76833 7.27212 6.13097 7.60509 5.17918L7.63599 5.09084Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="block text-xs mt-1 font-semibold text-brand-800">PASS</span>
            </div>
          </div>
        </div>
        <div class="flex justify-between w-4/5 pt-4 pb-4 pl-2 items-center">
          <div>
            <fieldset>
              <div class="leading-5">
                <div class="flex items-center">
                  <label class="block">
                    <span class="text-md font-semibold text-brand-gray-800 tracking-tight">
                      {{ membershipPass?.attributes?.name }}
                    </span>
                    <div class="text-xs tracking-wide text-brand-900">
                      <span v-if="membershipPass?.attributes?.price?.cents > 0">
                        Benefits apply to orders made in {{ membershipTier?.attributes?.tier_name }}
                      </span>
                      <span v-else>Free</span>
                    </div>
                    <!-- Cancel button -->
                    <div v-if="showManageOptions" class="mt-2">
                      <button
                        class="px-4 py-2 text-gray-brand-600 border border-transparent font-semibold text-xs rounded-full bg-gray-brand-100 hover:border-gray-brand-600"
                        @click="handleCancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </label>
                </div>
              </div>
            </fieldset>
          </div>
          <div class="flex items-center">
            <button
              class="items-center px-2.5 py-2 text-xs font-medium italic rounded-md bg-gray-brand-100 text-gray-brand-800 focus:outline-none ml-2 mr-6"
              @click="toggleManage"
            >
              {{ showManageOptions ? 'Close' : 'Manage' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Benefits Description -->
      <div class="w-full border-t w-full bg-brand-100 text-brand-800">
        <div
          class="leading-5 pt-4 pb-4 px-6 scrolling-touch text-sm text-center whitespace-pre-line max-h-[88px] overflow-hidden"
        >
          This membership pass renews {{ getBillingIntervalText(membershipPass) }}. It provides
          <template v-if="sortedMembershipPassRewards.length">
            <template v-for="(reward, index) in sortedMembershipPassRewards" :key="reward.id">
              {{ formatReward(reward)
              }}{{
                index === sortedMembershipPassRewards.length - 1
                  ? '.'
                  : index === sortedMembershipPassRewards.length - 2
                    ? ' & '
                    : ', '
              }}
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapRelationship from '@/utils/mapRelationship'
import mapRelationships from '@/utils/mapRelationships'
import Modal from '@/components/Molecules/Modal'

export default {
  name: 'PassOptionsBlock',
  components: {
    Modal
  },
  props: {
    membershipPass: {
      type: Object,
      required: true
    },
    membershipTier: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showManageOptions: false,
      showCancelModal: false,
      showFeedbackModal: false,
      showConfirmationModal: false,
      feedbackText: '',
      showFeedbackError: false
    }
  },
  computed: {
    membershipPassRewards() {
      return mapRelationships(this.$store.state, this.membershipPass?.relationships?.membership_pass_rewards?.data)
    },
    sortedMembershipPassRewards() {
      if (!this.membershipPassRewards) return []

      return [...this.membershipPassRewards]
        .filter((reward) => reward.attributes.type !== 'ImmediateMembershipPassReward')
        .sort((a, b) => {
          const typeOrder = {
            AccountCreditMembershipPassReward: 1,
            FulfillmentMembershipPassReward: 2
          }
          return typeOrder[a.attributes.type] - typeOrder[b.attributes.type]
        })
    }
  },
  methods: {
    getBillingIntervalText(pass) {
      const interval = pass?.attributes?.billing_interval
      switch (interval) {
        case 'weekly':
          return 'weekly'
        case 'monthly':
          return 'monthly'
        case 'yearly':
          return 'yearly'
        case 'every_two_weeks':
          return 'every two weeks'
        default:
          return interval || ''
      }
    },
    formatReward(reward) {
      return this.formatMembershipPassReward(reward)
    },
    formatMembershipPassReward(reward) {
      const attrs = reward.attributes
      const type = attrs.type
      const amount = attrs.credit_amount?.format
      const interval = attrs.credit_interval

      switch (type) {
        case 'ImmediateMembershipPassReward':
          return `${amount} off immediate discount`

        case 'AccountCreditMembershipPassReward': {
          if (interval === 'monthly') {
            return `${amount} account credit monthly`
          } else if (interval === 'weekly') {
            return `${amount} account credit weekly`
          } else if (interval === 'every_two_weeks') {
            return `${amount} account credit every two weeks`
          }
          return `${amount} account credit ${interval}`
        }

        case 'FulfillmentMembershipPassReward':
          return 'Free delivery on all orders'

        default:
          return 'Unknown reward type'
      }
    },
    toggleManage() {
      this.showManageOptions = !this.showManageOptions
      this.$emit('manage')
    },
    handleCancel() {
      this.showCancelModal = true
    },
    closeCancelModal() {
      this.showCancelModal = false
      this.showFeedbackModal = false
      this.showConfirmationModal = false
      this.showManageOptions = false
    },
    confirmCancel() {
      this.showCancelModal = false
      this.showFeedbackModal = true
    },
    submitFeedback() {
      if (!this.feedbackText.trim()) {
        this.showFeedbackError = true
        return
      }
      // Make API call to cancel membership pass
      this.$store
        .dispatch('updateCustomerInfo', {
          customer: {
            current_membership_pass_id: null
          }
        })
        .then(() => {
          // Emit event with pass details for confirmation modal
          this.$emit('cancellation-success', {
            passName: this.membershipPass?.attributes?.name,
            billingInterval: this.membershipPass?.attributes?.billing_interval,
            getBillingIntervalText: this.getBillingIntervalText(this.membershipPass)
          })
          this.feedbackText = ''
          this.showFeedbackError = false
          this.closeCancelModal()
        })
        .catch((error) => {
          console.error('Failed to cancel membership pass:', error)
        })
    }
  }
}
</script>
